import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AppBar, Toolbar, Typography, Button, Box, IconButton,
  Avatar, Chip, Tooltip, Badge, Popover,
  List, ListItem, ListItemText, ListItemAvatar, Divider,
  CircularProgress
} from '@mui/material';
import {
  Logout, Notifications, MenuOpen, Menu as MenuIcon,
  Dashboard, PersonAdd, LocalTaxi, DriveEta, People,
  CalendarToday, CardMembership, Map, AttachMoney, Feedback,
  DirectionsCar, NotificationsNone, DoneAll, Payment, TrendingUp
} from '@mui/icons-material';
import LanguageSelector from './LanguageSelector';

const PAGE_LABELS = {
  '/dashboard': { label: 'Dashboard', icon: <Dashboard sx={{ fontSize: 18 }} /> },
  '/analytics': { label: 'Business Analytics', icon: <TrendingUp sx={{ fontSize: 18 }} /> },
  '/owner-approval': { label: 'Owner Approval', icon: <PersonAdd sx={{ fontSize: 18 }} /> },
  '/driver-approval': { label: 'Driver Approval', icon: <LocalTaxi sx={{ fontSize: 18 }} /> },
  '/vehicle-approval': { label: 'Vehicle Approval', icon: <DriveEta sx={{ fontSize: 18 }} /> },
  '/driver-list': { label: 'All Drivers', icon: <People sx={{ fontSize: 18 }} /> },
  '/owner-list': { label: 'All Owners', icon: <People sx={{ fontSize: 18 }} /> },
  '/vehicle-list': { label: 'All Vehicles', icon: <DirectionsCar sx={{ fontSize: 18 }} /> },
  '/bookings': { label: 'Booking Management', icon: <CalendarToday sx={{ fontSize: 18 }} /> },
  '/membership-management': { label: 'Membership Plans', icon: <CardMembership sx={{ fontSize: 18 }} /> },
  '/zone-management': { label: 'Zone Management', icon: <Map sx={{ fontSize: 18 }} /> },
  '/rate-management': { label: 'Rate Management', icon: <AttachMoney sx={{ fontSize: 18 }} /> },
  '/zone-driver-pricing': { label: 'Zone Driver Pricing', icon: <TrendingUp sx={{ fontSize: 18 }} /> },
  '/feedback': { label: 'Feedback', icon: <Feedback sx={{ fontSize: 18 }} /> },
  '/razorpay-settings': { label: 'Razorpay Payments', icon: <Payment sx={{ fontSize: 18 }} /> },
};

const TYPE_COLORS = {
  driver: '#1976d2',
  owner: '#388e3c',
  vehicle: '#f57c00',
  membership: '#7b1fa2',
  booking: '#0288d1',
};

const formatTime = (time) => {
  const diff = Math.floor((Date.now() - new Date(time)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

const Navbar = ({ onLogout, sidebarOpen, onSidebarToggle }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [notifAnchor, setNotifAnchor] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [notifCount, setNotifCount] = useState(0);
  const [notifLoading, setNotifLoading] = useState(false);
  const [readIds, setReadIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem('readNotifIds') || '[]'); } catch { return []; }
  });

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get('/admin/notifications');
      if (data.success) {
        setNotifications(data.notifications || []);
        const unread = (data.notifications || []).filter(n => !readIds.includes(n.id));
        setNotifCount(unread.length);
      }
    } catch (e) {
      // Silently ignore; user is likely not logged in yet
    }
  }, [readIds]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000); // Poll every 30 seconds
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleNotifOpen = (e) => {
    setNotifAnchor(e.currentTarget);
    setNotifLoading(true);
    fetchNotifications().finally(() => setNotifLoading(false));
  };

  const handleNotifClose = () => setNotifAnchor(null);

  const handleMarkAllRead = () => {
    const allIds = notifications.map(n => n.id);
    setReadIds(allIds);
    localStorage.setItem('readNotifIds', JSON.stringify(allIds));
    setNotifCount(0);
  };

  const handleNotifClick = (notif) => {
    const newRead = [...new Set([...readIds, notif.id])];
    setReadIds(newRead);
    localStorage.setItem('readNotifIds', JSON.stringify(newRead));
    setNotifCount(prev => Math.max(0, prev - 1));
    setNotifAnchor(null);
    navigate(notif.link);
  };

  const pageInfo = PAGE_LABELS[location.pathname] || { label: 'DrainGo Admin', icon: null };
  const notifOpen = Boolean(notifAnchor);

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(12px)',
        borderBottom: '1px solid rgba(17,102,17,0.12)',
        color: '#1a1a2e',
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar sx={{ gap: 1 }}>
        {/* Sidebar Toggle */}
        <IconButton
          onClick={onSidebarToggle}
          size="small"
          sx={{
            color: '#116611',
            bgcolor: 'rgba(17,102,17,0.08)',
            borderRadius: '8px',
            mr: 1,
            '&:hover': { bgcolor: 'rgba(17,102,17,0.15)' },
          }}
        >
          {sidebarOpen ? <MenuOpen /> : <MenuIcon />}
        </IconButton>

        {/* Page Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
          <Box sx={{ color: '#116611', display: 'flex', alignItems: 'center' }}>{pageInfo.icon}</Box>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, fontSize: '1rem', color: '#116611', letterSpacing: '-0.3px' }}
          >
            {pageInfo.label}
          </Typography>
        </Box>

        {/* Notification Bell */}
        <Tooltip title="Notifications">
          <IconButton
            onClick={handleNotifOpen}
            size="small"
            sx={{
              color: notifCount > 0 ? '#116611' : '#546e7a',
              bgcolor: notifCount > 0 ? 'rgba(17,102,17,0.1)' : 'rgba(84,110,122,0.08)',
              borderRadius: '8px',
              '&:hover': { bgcolor: 'rgba(17,102,17,0.15)', color: '#116611' },
            }}
          >
            <Badge badgeContent={notifCount} color="error" max={99}>
              <Notifications fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Notifications Popover */}
        <Popover
          open={notifOpen}
          anchorEl={notifAnchor}
          onClose={handleNotifClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              width: 380,
              maxHeight: 480,
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
              mt: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
            }
          }}
        >
          {/* Header */}
          <Box sx={{
            px: 2.5, py: 1.8,
            background: 'linear-gradient(135deg, #116611 0%, #338800 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Notifications sx={{ color: 'white', fontSize: 20 }} />
              <Typography sx={{ fontWeight: 700, color: 'white', fontSize: '0.95rem' }}>
                Notifications
              </Typography>
              {notifCount > 0 && (
                <Chip
                  label={notifCount}
                  size="small"
                  sx={{ bgcolor: '#ff4444', color: 'white', fontWeight: 700, height: 20, fontSize: '0.7rem' }}
                />
              )}
            </Box>
            {notifCount > 0 && (
              <Tooltip title="Mark all as read">
                <IconButton size="small" onClick={handleMarkAllRead} sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  <DoneAll fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
            {notifLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={28} sx={{ color: '#116611' }} />
              </Box>
            ) : notifications.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 5, px: 2 }}>
                <NotificationsNone sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                <Typography color="textSecondary" variant="body2">
                  You're all caught up!
                </Typography>
              </Box>
            ) : (
              <List disablePadding>
                {notifications.map((n, idx) => {
                  const isUnread = !readIds.includes(n.id);
                  return (
                    <React.Fragment key={n.id}>
                      <ListItem
                        button
                        onClick={() => handleNotifClick(n)}
                        sx={{
                          px: 2.5, py: 1.5,
                          bgcolor: isUnread ? 'rgba(17,102,17,0.04)' : 'transparent',
                          '&:hover': { bgcolor: 'rgba(17,102,17,0.08)' },
                          cursor: 'pointer',
                          alignItems: 'flex-start',
                        }}
                      >
                        <ListItemAvatar sx={{ minWidth: 44 }}>
                          <Avatar
                            sx={{
                              width: 36, height: 36,
                              bgcolor: `${TYPE_COLORS[n.type]}15`,
                              fontSize: '1.1rem',
                              border: `1.5px solid ${TYPE_COLORS[n.type]}40`
                            }}
                          >
                            {n.icon}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: isUnread ? 700 : 500, color: '#1a1a2e', fontSize: '0.82rem' }}
                              >
                                {n.title}
                              </Typography>
                              <Typography variant="caption" sx={{ color: '#9e9e9e', ml: 1, whiteSpace: 'nowrap' }}>
                                {formatTime(n.time)}
                              </Typography>
                            </Box>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: '#607d8b', lineHeight: 1.4, display: 'block' }}>
                              {n.message}
                            </Typography>
                          }
                        />
                        {isUnread && (
                          <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            bgcolor: '#116611', ml: 1, mt: 1, flexShrink: 0
                          }} />
                        )}
                      </ListItem>
                      {idx < notifications.length - 1 && <Divider sx={{ mx: 2 }} />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>

          {/* Footer */}
          {notifications.length > 0 && (
            <Box sx={{ px: 2.5, py: 1.2, borderTop: '1px solid #f0f0f0', bgcolor: '#fafafa' }}>
              <Typography
                variant="caption"
                sx={{ color: '#116611', fontWeight: 600, cursor: 'pointer' }}
                onClick={() => { handleNotifClose(); navigate('/driver-approval'); }}
              >
                View all pending approvals →
              </Typography>
            </Box>
          )}
        </Popover>

        {/* Language */}
        <Tooltip title="Language">
          <Box>
            <LanguageSelector
              sx={{
                color: '#546e7a',
                bgcolor: 'rgba(84,110,122,0.08)',
                borderRadius: '8px',
                '&:hover': { bgcolor: 'rgba(84,110,122,0.14)', color: '#116611' },
              }}
              iconButtonProps={{ size: 'small' }}
              menuProps={{
                PaperProps: { sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)', mt: 1 } }
              }}
            />
          </Box>
        </Tooltip>

        {/* Admin Chip */}
        <Chip
          avatar={<Avatar sx={{ bgcolor: '#116611', width: 26, height: 26, fontSize: '0.75rem' }}>A</Avatar>}
          label="Admin"
          size="small"
          sx={{
            bgcolor: 'rgba(17,102,17,0.08)',
            color: '#116611',
            fontWeight: 600,
            fontSize: '0.8rem',
            borderRadius: '8px',
            border: '1px solid rgba(17,102,17,0.15)',
          }}
        />

        {/* Logout */}
        <Tooltip title={t('logout')}>
          <Button
            onClick={onLogout}
            size="small"
            startIcon={<Logout fontSize="small" />}
            sx={{
              color: '#c62828',
              bgcolor: 'rgba(198,40,40,0.07)',
              borderRadius: '8px',
              fontWeight: 600,
              fontSize: '0.8rem',
              border: '1px solid rgba(198,40,40,0.15)',
              '&:hover': { bgcolor: 'rgba(198,40,40,0.14)', border: '1px solid rgba(198,40,40,0.3)' },
              px: 1.5,
            }}
          >
            {t('logout')}
          </Button>
        </Tooltip>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
